import React from "react";
import {
  Avatar,
  Box,
  Stack,
  Typography,
  Chip,
  Alert,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  LinearProgress,
  Button,
} from "@mui/material";
import Level from "../../Assets/Level.svg";
import StarsIcon from "@mui/icons-material/Stars";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import "./Profile.css";

const AvatarSection = ({ data, imageChange, shouldShowSubscriptionMessage, formatExpirationDate, onCancelSubscription, cancelling }) => {
  // Calculate level progress (assuming 15 levels max)
  const maxLevel = 15;
  const currentLevel = data?.level || 1;
  const levelProgress = (currentLevel / maxLevel) * 100;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Profile Header Card */}
      <Card 
        elevation={2}
        sx={{ 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: { xs: "flex-start", sm: "center" }, 
            gap: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            {/* Avatar with edit overlay */}
            <Box sx={{ 
              position: 'relative',
              alignSelf: { xs: 'center', sm: 'flex-start' }
            }}>
              <Avatar
                alt="Profile Picture"
                src={data?.profile_image_url}
                sx={{ 
                  width: { xs: 80, sm: 100 }, 
                  height: { xs: 80, sm: 100 }, 
                  border: '4px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
              <Tooltip title="Change profile picture">
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: -5,
                    right: -5,
                    backgroundColor: 'white',
                    color: '#667eea',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                  }}
                  onClick={() => document.getElementById("icon-button-file").click()}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <input
                accept="image/*"
                id="icon-button-file"
                type="file"
                onChange={imageChange}
                style={{ display: "none" }}
              />
            </Box>

            {/* User Info */}
            <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}
                >
                  {data?.first_name} {data?.last_name}
                </Typography>
                {data?.title && (
                  <Box className="medal-container">
                    <Box className={`medal ${data.title.toLowerCase()}`}>
                      <span className="medal-text">{data.title.toUpperCase()}</span>
                      <Box className="shimmer"></Box>
                    </Box>
                  </Box>
                )}
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  opacity: 0.9, 
                  mb: 2, 
                  color: 'white',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  wordBreak: 'break-word'
                }}
              >
                {data?.email}
              </Typography>
              
               {/* Level Progress */}
               <Box sx={{ 
                 display: 'flex', 
                 alignItems: 'center', 
                 gap: { xs: 1, sm: 2 },
                 flexDirection: { xs: 'column', sm: 'row' },
                 width: '100%'
               }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <Avatar src={Level} sx={{ width: { xs: 18, sm: 20 }, height: { xs: 18, sm: 20 } }} />
                   <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                     Level {currentLevel}
                   </Typography>
                 </Box>
                 <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 100 }, width: { xs: '100%', sm: 'auto' } }}>
                   <LinearProgress 
                     variant="determinate" 
                     value={levelProgress} 
                     sx={{ 
                       height: { xs: 5, sm: 6 }, 
                       borderRadius: 3,
                       backgroundColor: 'rgba(255,255,255,0.3)',
                       '& .MuiLinearProgress-bar': {
                         backgroundColor: 'white'
                       }
                     }} 
                   />
                 </Box>
               </Box>
               
               {/* Level Title */}
               <Box sx={{ 
                 mt: 1.5, 
                 display: 'flex', 
                 alignItems: 'center', 
                 gap: 1,
                 justifyContent: { xs: 'center', sm: 'flex-start' },
                 flexWrap: 'wrap'
               }}>
                 <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                   Level Title:
                 </Typography>
                 <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                   {data?.level_title || "Pathfinder"}
                 </Typography>
               </Box>
            </Box>

            {/* Points Display */}
            <Chip
              icon={<StarsIcon />}
              label={`${data?.points || 0} Points`}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                height: { xs: 36, sm: 40 },
                alignSelf: { xs: 'center', sm: 'flex-start' },
                '& .MuiChip-icon': {
                  color: 'white',
                  fontSize: { xs: '1rem', sm: '1.2rem' }
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Plan Information Card */}
      <Card elevation={1} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CardMembershipIcon color="primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Subscription Plan
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Current Plan:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {data?.plan_name || "Free Plan"}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Expires:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {data?.plan_expiration_date
                  ? new Date(data?.plan_expiration_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })
                  : "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Credits left:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {Math.round(data?.credits_left ?? 0)}
              </Typography>
            </Box>
          </Box>
          
          {/* Subscription Status Message */}
          {shouldShowSubscriptionMessage && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              You have canceled your subscription, but {data?.previous_plan_name || data?.plan_name || 'your plan'} benefits will be active until {formatExpirationDate(data.plan_expiration_date)}.
            </Alert>
          )}
          
          {/* Cancel Subscription Button */}
          {data?.subscription_status === 'active' && data?.plan_name && data?.plan_name !== 'Free' && (
            <Button
              variant="outlined"
              color="error"
              onClick={onCancelSubscription}
              disabled={cancelling}
              sx={{ 
                mt: 2,
                width: '100%',
                opacity: cancelling ? 0.6 : 1
              }}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AvatarSection;
